import React, { useState, useEffect, useCallback } from 'react';
import { getFullBalance, getOpenOrders, cancelOrder, getActiveTargets, sellNow, convertToUsdc } from '../../services/ExchangeService';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import CandleChart from '../Dashboard/CandleChart';
import PageHeader from '../../components/PageHeader/PageHeader';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';
import { fmtMoney, fmtPct, fmtPrice, fmtQty } from '../../utils/format';

// Skeleton placeholder rows shown while a table is loading.
function SkeletonRows({ rows = 4, cols }) {
    return Array.from({ length: rows }).map((_, r) => (
        <tr key={`skel-${r}`}>
            {Array.from({ length: cols }).map((__, c) => (
                <td key={c}><span className="gh-skel" /></td>
            ))}
        </tr>
    ));
}

/**
 * Positions — surfaces the user's REAL Binance spot state:
 *  1. Holdings (assets valued in USDC)
 *  2. Open orders (resting limit / take-profit orders the bot left on the exchange),
 *     each cancellable. This closes the gap where the no-loss "Today" bot leaves
 *     resting take-profit limit orders that had no UI to view/cancel.
 */
function Positions() {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    // USDC is the platform quote — value holdings in USDC.
    const FIAT = 'USDC';

    const [holdings, setHoldings] = useState([]);
    const [orders, setOrders] = useState([]);
    const [targets, setTargets] = useState([]);
    const [isLoadingHoldings, setIsLoadingHoldings] = useState(true);
    const [isLoadingOrders, setIsLoadingOrders] = useState(true);
    const [isLoadingTargets, setIsLoadingTargets] = useState(true);
    const [notification, setNotification] = useState({ type: '', text: '' });
    // Symbol shown in the chart above the targets table (defaults to first row).
    const [chartSymbol, setChartSymbol] = useState(null);
    // Confirmation modals replace window.confirm for money-moving actions.
    const [sellConfirm, setSellConfirm] = useState(null);   // { tg }
    const [cancelConfirm, setCancelConfirm] = useState(null); // { order }
    const [convertConfirm, setConvertConfirm] = useState(null); // { asset }

    // "+0.02 (+0.19%)" with green/red coloring based on sign.
    const renderPnl = (pnl, pnlPct) => {
        const n = parseFloat(pnl);
        const p = parseFloat(pnlPct);
        if (isNaN(n) && isNaN(p)) return <span className="text-muted">—</span>;
        const positive = (isNaN(n) ? p : n) >= 0;
        const sign = positive ? '+' : '';
        const cls = positive ? 'gh-pos fw-bold' : 'gh-neg fw-bold';
        return (
            <span className={cls}>
                {!isNaN(n) ? `${sign}${fmtMoney(n)}` : ''}
                {!isNaN(p) ? ` (${sign}${fmtPct(p)}%)` : ''}
            </span>
        );
    };

    // One-click convert a spot holding to USDC (cancels its resting orders + market-sells).
    function doConvert(asset) {
        setConvertConfirm(null);
        convertToUsdc(asset)
            .then(() => { setNotification({ type: 'success', text: t('positions.converted') }); loadHoldings(); loadOrders(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    // Compute { positive, text } for a target's current P&L (for the modal).
    const pnlInfo = (tg) => {
        const pnl = parseFloat(tg.unrealizedPnl);
        const pnlPct = parseFloat(tg.unrealizedPnlPct);
        const positive = isNaN(pnlPct) ? (isNaN(pnl) ? true : pnl >= 0) : pnlPct >= 0;
        const text = `${!isNaN(pnl) ? (pnl >= 0 ? '+' : '') + fmtMoney(pnl) : '—'}${!isNaN(pnlPct) ? ` (${pnlPct >= 0 ? '+' : ''}${fmtPct(pnlPct)}%)` : ''}`;
        return { positive, text };
    };

    const loadHoldings = useCallback(() => {
        setIsLoadingHoldings(true);
        getFullBalance(FIAT, false)
            .then((info) => {
                const rows = Object.entries(info)
                    // keep only per-asset objects (drop the top-level fiatEstimate string)
                    .filter(item => typeof info[item[0]] === 'object' && item[0] !== 'fiatEstimate')
                    .map(item => ({
                        asset: item[0],
                        available: parseFloat(item[1].available) || 0,
                        onOrder: parseFloat(item[1].onOrder) || 0,
                        fiatEstimate: item[1].fiatEstimate
                    }))
                    // hide dust / zero balances
                    .filter(b => (b.available + b.onOrder) > 0 && parseFloat(b.fiatEstimate) >= 0.01)
                    // biggest position first
                    .sort((a, b) => (parseFloat(b.fiatEstimate) || 0) - (parseFloat(a.fiatEstimate) || 0));

                setHoldings(rows);
                setIsLoadingHoldings(false);
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
                setIsLoadingHoldings(false);
            });
    }, []);

    const loadOrders = useCallback(() => {
        setIsLoadingOrders(true);
        getOpenOrders()
            .then((data) => {
                const rows = (data || []).slice()
                    .sort((a, b) => (b.time || 0) - (a.time || 0));
                setOrders(rows);
                setIsLoadingOrders(false);
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
                setIsLoadingOrders(false);
            });
    }, []);

    const loadTargets = useCallback(() => {
        setIsLoadingTargets(true);
        getActiveTargets()
            .then((data) => {
                const rows = Array.isArray(data) ? data : [];
                setTargets(rows);
                // Default the chart to the first position; keep current selection if still present.
                setChartSymbol(prev => {
                    if (prev && rows.some(r => r.symbol === prev)) return prev;
                    return rows.length ? rows[0].symbol : prev;
                });
                setIsLoadingTargets(false);
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setIsLoadingTargets(false);
            });
    }, []);

    useEffect(() => {
        loadHoldings();
        loadOrders();
        loadTargets();
    }, [loadHoldings, loadOrders, loadTargets]);

    // Live updates: refresh targets (price / P&L) every 5s. Cleared on unmount.
    useEffect(() => {
        const id = setInterval(() => { loadTargets(); }, 5000);
        return () => clearInterval(id);
    }, [loadTargets]);

    function doCancel(order) {
        setCancelConfirm(null);
        cancelOrder(order.symbol, order.orderId)
            .then(() => {
                setNotification({ type: 'success', text: t('positions.cancelled') });
                loadOrders();
                loadHoldings();
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function doSellNow(tg) {
        setSellConfirm(null);
        sellNow(tg.engine, tg.sessionId)
            .then(() => {
                setNotification({ type: 'success', text: t('positions.sold') });
                loadTargets();
                loadHoldings();
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <PageHeader as="h2" title={t('positions.title')} />

                {chartSymbol ? <CandleChart symbol={chartSymbol} /> : null}

                <div className="card card-body border-0 shadow table-wrapper table-responsive mb-4">
                    <h2 className="fs-5 fw-bold mb-1">{t('positions.targets')}</h2>
                    <p className="small text-muted mb-3">{t('positions.targetsHint')}</p>
                    <table className="table table-hover gh-table-stack">
                        <thead>
                            <tr>
                                <th className="border-gray-200">{t('positions.symbol')}</th>
                                <th className="border-gray-200">{t('positions.bot')}</th>
                                <th className="border-gray-200 num">{t('positions.qty')}</th>
                                <th className="border-gray-200 num">{t('positions.avgBuy')}</th>
                                <th className="border-gray-200 num">{t('positions.current')}</th>
                                <th className="border-gray-200 num">{t('positions.pnlNet')}</th>
                                <th className="border-gray-200 num" title={t('positions.feesHint')}>{t('positions.fees')}</th>
                                <th className="border-gray-200 num">{t('positions.breakEven')}</th>
                                <th className="border-gray-200 num">{t('positions.targetSell')}</th>
                                <th className="border-gray-200 num">{t('positions.toGo')}</th>
                                <th className="border-gray-200 num">{t('positions.stop')}</th>
                                <th className="border-gray-200">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                isLoadingTargets && !targets.length
                                    ? <SkeletonRows rows={3} cols={12} />
                                    : targets.length
                                        ? targets.map(tg => (
                                            <tr
                                                key={`tgt-${tg.engine}-${tg.sessionId}`}
                                                className={`gh-row-click${chartSymbol === tg.symbol ? ' table-active' : ''}`}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={tg.symbol}
                                                onClick={() => setChartSymbol(tg.symbol)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChartSymbol(tg.symbol); } }}>
                                                <td data-label={t('positions.symbol')} className="fw-bold">{tg.symbol}</td>
                                                <td data-label={t('positions.bot')}><span className={`gh-badge ${tg.engine === 'TODAY' ? 'gh-badge-info' : 'gh-badge-gold'}`}>{tg.engine === 'TODAY' ? 'Today' : 'Risk'}</span></td>
                                                <td data-label={t('positions.qty')} className="num">{fmtQty(tg.qty)}</td>
                                                <td data-label={t('positions.avgBuy')} className="num">{fmtPrice(tg.avgEntry)}</td>
                                                <td data-label={t('positions.current')} className="num fw-bold">{tg.currentPrice != null ? fmtPrice(tg.currentPrice) : '—'}</td>
                                                <td data-label={t('positions.pnlNet')} className="num">{renderPnl(tg.unrealizedPnl, tg.unrealizedPnlPct)}</td>
                                                {/* Fee transparency: entry fee already paid + exit fee if sold now. */}
                                                <td data-label={t('positions.fees')} className="num text-muted" title={t('positions.feesHint')}>
                                                    {tg.entryFeePaid != null
                                                        ? `${fmtMoney(tg.entryFeePaid)} + ${tg.exitFeeNow != null ? fmtMoney(tg.exitFeeNow) : '—'}`
                                                        : '—'}
                                                </td>
                                                <td data-label={t('positions.breakEven')} className="num text-muted">{tg.breakEven != null ? fmtPrice(tg.breakEven) : '—'}</td>
                                                <td data-label={t('positions.targetSell')} className="num fw-bold">{fmtPrice(tg.targetSell)} <span className="text-muted">(+{fmtPct(tg.takeProfitPct)}%)</span>{tg.trailing ? <span className="gh-badge gh-badge-gold ms-1">{t('positions.trailing')}</span> : null}</td>
                                                <td data-label={t('positions.toGo')} className={`num ${tg.pctToGo != null && tg.pctToGo <= 0 ? 'gh-pos fw-bold' : ''}`}>{tg.pctToGo != null ? `${tg.pctToGo > 0 ? '+' : ''}${fmtPct(tg.pctToGo)}%` : '—'}</td>
                                                <td data-label={t('positions.stop')} className="num gh-neg">{tg.stopPrice ? `${fmtPrice(tg.stopPrice)} (−${fmtPct(tg.stopLossPct)}%)` : '—'}</td>
                                                <td data-label={t('common.actions')}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger py-0 px-2"
                                                        onClick={(e) => { e.stopPropagation(); setSellConfirm({ tg }); }}>
                                                        {t('positions.sellNow')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                        : (<tr><td colSpan={12} className="text-muted">{t('positions.noTargets')}</td></tr>)
                            }
                        </tbody>
                    </table>
                </div>

                <div className="card card-body border-0 shadow table-wrapper table-responsive mb-4">
                    <h2 className="fs-5 fw-bold mb-3">{t('positions.holdings')}</h2>
                    <table className="table table-hover gh-table-stack">
                        <thead>
                            <tr>
                                <th className="border-gray-200">{t('positions.asset')}</th>
                                <th className="border-gray-200 num">{t('positions.qty')}</th>
                                <th className="border-gray-200 num">{t('positions.value')}</th>
                                <th className="border-gray-200">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                isLoadingHoldings
                                    ? <SkeletonRows rows={4} cols={4} />
                                    : holdings.length
                                        ? holdings.map(h => (
                                            <tr key={`hold-${h.asset}`}>
                                                <td data-label={t('positions.asset')} className="fw-bold">
                                                    <img className="me-2" width={16} alt="" onError={(e) => { e.target.style.display = 'none'; }} src={`/img/icons/black/${h.asset.toLowerCase()}.svg`} />
                                                    {h.asset}
                                                </td>
                                                <td data-label={t('positions.qty')} className="num">{fmtQty(h.available + h.onOrder)}</td>
                                                <td data-label={t('positions.value')} className="num fw-bold gh-neutral">{fmtMoney(h.fiatEstimate)}</td>
                                                <td data-label={t('common.actions')}>
                                                    {h.asset !== 'USDC' && (
                                                        <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2" onClick={() => setConvertConfirm({ asset: h.asset })}>
                                                            {t('positions.convert')}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                        : (<tr><td colSpan={4} className="text-muted">{t('positions.noHoldings')}</td></tr>)
                            }
                        </tbody>
                    </table>
                </div>

                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <h2 className="fs-5 fw-bold mb-3">{t('positions.openOrders')}</h2>
                    <table className="table table-hover gh-table-stack">
                        <thead>
                            <tr>
                                <th className="border-gray-200">{t('positions.symbol')}</th>
                                <th className="border-gray-200">{t('positions.side')}</th>
                                <th className="border-gray-200">{t('positions.type')}</th>
                                <th className="border-gray-200 num">{t('positions.price')}</th>
                                <th className="border-gray-200 num">{t('positions.qty')}</th>
                                <th className="border-gray-200">{t('positions.status')}</th>
                                <th className="border-gray-200 num">{t('positions.time')}</th>
                                <th className="border-gray-200">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                isLoadingOrders
                                    ? <SkeletonRows rows={4} cols={8} />
                                    : orders.length
                                        ? orders.map(o => (
                                            <tr key={`order-${o.symbol}-${o.orderId}`}>
                                                <td data-label={t('positions.symbol')} className="fw-bold">{o.symbol}</td>
                                                <td data-label={t('positions.side')} className={o.side === 'BUY' ? 'gh-pos fw-bold' : 'gh-neg fw-bold'}>{o.side}</td>
                                                <td data-label={t('positions.type')}>{o.type}</td>
                                                <td data-label={t('positions.price')} className="num">{fmtPrice(o.price)}{o.stopPrice && parseFloat(o.stopPrice) > 0 ? ` (${fmtPrice(o.stopPrice)})` : ''}</td>
                                                <td data-label={t('positions.qty')} className="num">{fmtQty(o.executedQty)}/{fmtQty(o.origQty)}</td>
                                                <td data-label={t('positions.status')}>{o.status}</td>
                                                <td data-label={t('positions.time')} className="num">{o.time ? new Date(o.time).toLocaleString() : '—'}</td>
                                                <td data-label={t('common.actions')}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger py-0 px-2"
                                                        onClick={() => setCancelConfirm({ order: o })}>
                                                        {t('positions.cancel')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                        : (<tr><td colSpan={8} className="text-muted">{t('positions.noOrders')}</td></tr>)
                            }
                        </tbody>
                    </table>
                </div>

                <Footer />
            </main>

            <ConfirmModal
                open={!!sellConfirm}
                title={t('positions.sellNowConfirm')}
                message={sellConfirm ? sellConfirm.tg.symbol : ''}
                details={sellConfirm ? [
                    { label: t('positions.qty'), value: fmtQty(sellConfirm.tg.qty) },
                    { label: t('positions.current'), value: fmtPrice(sellConfirm.tg.currentPrice) }
                ] : []}
                pnl={sellConfirm ? pnlInfo(sellConfirm.tg) : null}
                confirmLabel={t('positions.sellNow')}
                onConfirm={() => doSellNow(sellConfirm.tg)}
                onCancel={() => setSellConfirm(null)}
            />

            <ConfirmModal
                open={!!cancelConfirm}
                title={t('positions.cancelConfirm')}
                message={cancelConfirm ? `${cancelConfirm.order.symbol} · ${cancelConfirm.order.type}` : ''}
                confirmLabel={t('positions.cancel')}
                onConfirm={() => doCancel(cancelConfirm.order)}
                onCancel={() => setCancelConfirm(null)}
            />

            <ConfirmModal
                open={!!convertConfirm}
                title={t('positions.convertConfirm')}
                message={convertConfirm ? `${convertConfirm.asset} → USDC` : ''}
                confirmLabel={t('positions.convert')}
                onConfirm={() => doConvert(convertConfirm.asset)}
                onCancel={() => setConvertConfirm(null)}
            />

            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Positions;
